import { useCallback, useEffect, useMemo, useRef } from "react"
import { createSearchParams, useSearchParams } from "react-router-dom"
import { pick, omitBy, isNull } from "lodash"
import { parse } from "helpers/string"

export const toAccessSearchString = (object, accessAttributes = []) =>
  createSearchParams(omitBy(pick(object, accessAttributes), isNull)).toString()

export const searchParamsToObject = (searchParams, { accessAttributes, skipEmpty = true } = {}) => {
  const keys = accessAttributes || searchParams.keys()

  const params = keys.reduce((acc, key) => {
    const arrRegex = /\[\]$/g
    const isArr = key.match(arrRegex)?.length
    const value = isArr ? searchParams.getAll(key).map(parse) : parse(searchParams.get(key))
    return { ...acc, [key]: value }
  }, {})

  return skipEmpty ? omitBy(params, isNull) : params
}

export const accessParams = (searchParams, accessAttributes) =>
  accessAttributes.reduce((acc, key) => {
    const arrRegex = /\[\]$/g
    const isArr = key.match(arrRegex)?.length
    const value = isArr ? searchParams.getAll(key).map(parse) : parse(searchParams.get(key))
    return { ...acc, [key]: value }
  }, {})

export default function useSearchFilter({ accessAttributes = [], exceptDependencies = [], additionalDependencies = [] }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const accessedParams = useMemo(
    () =>
      searchParamsToObject(searchParams, {
        accessAttributes,
        skipEmpty: false
      }),
    [searchParams, accessAttributes]
  )

  const accessedParamsFiltered = useMemo(() => omitBy(accessedParams, isNull), [accessedParams])

  const updatedCallbackRef = useRef(null)
  const isFirstRun = useRef(true)

  const setCallback = (cb) => {
    if (typeof cb === "function") {
      updatedCallbackRef.current = cb
    }
  }

  const updateSearchFilters = useCallback(
    (params) => {
      console.log("Updating search filters with params:", params)
      setSearchParams(
        (prevParams) => {
          const accessed = accessParams(prevParams, accessAttributes)
          const newParams = omitBy(
            {
              ...omitBy(accessed, isNull),
              ...pick(params, accessAttributes)
            },
            isNull
          )
          return newParams
        },
        { replace: true }
      )
    },
    [accessAttributes, setSearchParams]
  )

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }

    if (updatedCallbackRef.current) {
      updatedCallbackRef.current(accessedParamsFiltered, additionalDependencies)
    }
  }, [accessedParamsFiltered, ...additionalDependencies]) // ✅ Watch latest params only

  return [accessedParamsFiltered, updateSearchFilters, setCallback]
}
